import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, KeyRound, Settings2, Layers, Plug2, ShieldCheck, Wrench, CircleHelp,
  Check, X, Lock, Shield, Timer, RefreshCw,
} from 'lucide-react';
import { Card } from '../components/Card';

export function Help() {
  const [activeCategory, setActiveCategory] = useState<string>('getting-started');

  useEffect(() => {
    const handleChangeCategory = (e: Event) => {
      setActiveCategory((e as CustomEvent<string>).detail);
    };
    window.addEventListener('changeHelpCategory', handleChangeCategory);
    return () => window.removeEventListener('changeHelpCategory', handleChangeCategory);
  }, []);

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: Rocket },
    { id: 'identities', name: 'Identities & Aliases', icon: KeyRound },
    { id: 'contexts', name: 'Contexts', icon: Plug2 },
    { id: 'bulk-operations', name: 'Bulk Operations', icon: Layers },
    { id: 'api', name: 'API Integration', icon: Settings2 },
    { id: 'security', name: 'Security & Privacy', icon: ShieldCheck },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: Wrench },
    { id: 'faq', name: 'FAQ', icon: CircleHelp },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">Help & Support</h1>
        <p className="text-muted-foreground">
          Everything you need to know about using Varinym
        </p>
      </div>

      {/* Contact Support Banner */}
      <div className="bg-muted border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Need Additional Help?</h3>
            <p className="text-muted-foreground mb-4">
              Our support team is here to assist you with any questions or issues.
            </p>
            <div className="flex gap-4 text-sm">
              <div>
                <span className="font-medium text-foreground">Email:</span>{' '}
                <a href="mailto:support@varinym.com" className="text-primary hover:underline">
                  support@varinym.com
                </a>
              </div>
              <div>
                <span className="font-medium text-foreground">Response Time:</span>{' '}
                <span className="text-muted-foreground">Within 24 hours</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <nav className="space-y-1">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex w-full items-center gap-2.5 text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeCategory === category.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {category.name}
                  </button>
                );
              })}
            </nav>
          </Card>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {activeCategory === 'getting-started' && <GettingStarted />}
          {activeCategory === 'identities' && <IdentitiesHelp />}
          {activeCategory === 'contexts' && <ContextsHelp />}
          {activeCategory === 'bulk-operations' && <BulkOperationsHelp />}
          {activeCategory === 'api' && <APIHelp />}
          {activeCategory === 'security' && <SecurityHelp />}
          {activeCategory === 'troubleshooting' && <TroubleshootingHelp />}
          {activeCategory === 'faq' && <FAQ />}
        </div>
      </div>
    </div>
  );
}

function GettingStarted() {
  return (
    <>
      <Card title="Welcome to Varinym">
        <div className="prose prose-sm max-w-none text-foreground">
          <p className="text-muted-foreground mb-4">
            Varinym is a privacy-first platform that helps you protect
            user identities by creating a distinct masked identifier (alias) for each context.
          </p>

          <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Core Concepts</h3>

          <div className="space-y-4">
            <div className="bg-accent/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Identity</h4>
              <p className="text-sm text-muted-foreground">
                A unique record that represents a user in your system. Each identity is associated with a
                <code className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs font-mono">user_id</code>
                and is the protected identity that aliases are issued for.
              </p>
            </div>

            <div className="bg-accent/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Alias</h4>
              <p className="text-sm text-muted-foreground">
                A context-specific masked identifier generated from an identity. Aliases can be configured
                to expire and rotate automatically, providing enhanced security for different contexts.
              </p>
            </div>

            <div className="bg-accent/50 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Context</h4>
              <p className="text-sm text-muted-foreground">
                An alias context is an isolation boundary in which Varinym creates a distinct alias for an
                identity. A context can represent an application, vendor, environment, workload, recipient,
                or processing purpose, and defines how its aliases behave (format, expiration, auto-rotation).
                Each context receives a different alias.
              </p>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Quick Start Guide</h3>

          <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">Set up a Context:</span> Navigate to{' '}
              <Link to="/contexts" className="text-primary hover:underline">Contexts</Link> and create
              your first context configuration
            </li>
            <li>
              <span className="font-medium text-foreground">Create Identities:</span> Go to{' '}
              <Link to="/identities" className="text-primary hover:underline">Identities</Link> and
              create identities with aliases for your users
            </li>
            <li>
              <span className="font-medium text-foreground">Bulk Operations:</span> For large datasets, use{' '}
              <Link to="/bulk" className="text-primary hover:underline">Bulk Operations</Link> to
              upload CSV files
            </li>
            <li>
              <span className="font-medium text-foreground">API Integration:</span> Generate API keys in{' '}
              <Link to="/settings" className="text-primary hover:underline">Settings</Link> for
              programmatic access
            </li>
          </ol>
        </div>
      </Card>

      <Card title="Video Tutorial">
        <div className="bg-muted rounded-lg aspect-video flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className="text-sm">Video tutorial coming soon</p>
          </div>
        </div>
      </Card>
    </>
  );
}

function IdentitiesHelp() {
  return (
    <>
      <Card title="Understanding Identities & Aliases">
        <div className="prose prose-sm max-w-none">
          <h3 className="text-lg font-semibold text-foreground mb-3">What is an Identity?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            An identity is the core record that represents a user in Varinym. When you create an identity,
            you provide a <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">user_id</code>
            (typically an email or unique identifier), and Varinym generates:
          </p>

          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground mb-6">
            <li>A permanent <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">record_id</code> (internal identifier)</li>
            <li>A <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">default</code> alias (the alias for the reserved default context)</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3">What is an Alias?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            An alias is a context-specific masked identifier. Each alias is linked to an identity
            and a context. Aliases provide:
          </p>

          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground mb-6">
            <li>Context isolation (a different alias ID for each context)</li>
            <li>Expiration control (can be configured to expire)</li>
            <li>Automatic rotation (when configured on the context)</li>
            <li>Enhanced privacy and security</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3">Creating Identities</h3>
          <div className="bg-accent/50 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-foreground mb-2">Single Identity Creation:</p>
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
              <li>Navigate to <Link to="/identities" className="text-primary hover:underline">Identities</Link></li>
              <li>Enter the user ID (email or unique identifier)</li>
              <li>Select one or more contexts to create aliases</li>
              <li>Click "Create Identity"</li>
            </ol>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3">Looking Up Aliases</h3>
          <p className="text-sm text-muted-foreground mb-4">
            You can resolve an alias ID back to the original user ID:
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
            <li>Navigate to <Link to="/identities/search" className="text-primary hover:underline">Identities → Search</Link></li>
            <li>Switch "Search by" to Alias ID and enter the alias ID</li>
            <li>The system will return the associated identity and its aliases</li>
          </ol>

          <h3 className="text-lg font-semibold text-foreground mb-3 mt-6">Adding an Alias to an Existing Identity</h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-muted-foreground">
            <li>Navigate to <Link to="/identities/add-alias" className="text-primary hover:underline">Identities → Add Alias</Link></li>
            <li>Enter the identity's user ID and select a context</li>
            <li>Click "Add Alias" — if one already exists for that context, its existing ID is returned instead</li>
          </ol>
        </div>
      </Card>

      <Card title="Best Practices">
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <Check className="h-5 w-5 shrink-0 text-success" />
            <div>
              <span className="font-medium text-foreground">Use email addresses as user IDs</span>
              <p className="text-muted-foreground">Emails are unique and easy to track</p>
            </div>
          </li>
          <li className="flex gap-3">
            <Check className="h-5 w-5 shrink-0 text-success" />
            <div>
              <span className="font-medium text-foreground">Create aliases for each context</span>
              <p className="text-muted-foreground">Isolate user identities across different integrations</p>
            </div>
          </li>
          <li className="flex gap-3">
            <Check className="h-5 w-5 shrink-0 text-success" />
            <div>
              <span className="font-medium text-foreground">Store alias IDs, not user IDs</span>
              <p className="text-muted-foreground">Use alias IDs when sharing data with third parties</p>
            </div>
          </li>
        </ul>
      </Card>
    </>
  );
}

function ContextsHelp() {
  return (
    <>
      <Card title="Context Configuration">
        <div className="prose prose-sm max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            Contexts define how aliases behave. Each context has configurable properties that control
            privacy and security settings.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3">Context Properties</h3>

          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Context Name</h4>
              <p className="text-sm text-muted-foreground">
                A unique identifier for the context (e.g., "marketing", "analytics", "crm").
                The "default" context is created automatically and cannot be deleted.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Expiration (seconds)</h4>
              <p className="text-sm text-muted-foreground mb-2">
                How long aliases remain valid. Options:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li><code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">0</code> - Never expires (permanent)</li>
                <li><code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">86400</code> - 1 day</li>
                <li><code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">604800</code> - 1 week</li>
                <li><code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">2592000</code> - 30 days</li>
                <li>Custom value in seconds</li>
              </ul>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Auto-Rotation</h4>
              <p className="text-sm text-muted-foreground">
                When enabled, expired aliases are automatically regenerated when accessed.
                This provides enhanced security by regularly changing masked identifiers.
              </p>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Active Status</h4>
              <p className="text-sm text-muted-foreground">
                Inactive contexts cannot have new aliases created. Existing aliases remain accessible.
              </p>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Creating a Context</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Navigate to <Link to="/contexts" className="text-primary hover:underline">Contexts</Link></li>
            <li>Click "Create Context"</li>
            <li>Enter a unique context name</li>
            <li>Set expiration time (0 for permanent)</li>
            <li>Enable/disable auto-rotation</li>
            <li>Click "Create"</li>
          </ol>
        </div>
      </Card>

      <Card title="Common Context Configurations">
        <div className="space-y-4">
          <div className="bg-accent/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Default / Internal Use</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Expiration: <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">0</code> (never)</li>
              <li>Rotation: Disabled</li>
              <li>Use case: Long-term internal tracking</li>
            </ul>
          </div>

          <div className="bg-accent/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Marketing / Analytics</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Expiration: <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">2592000</code> (30 days)</li>
              <li>Rotation: Enabled</li>
              <li>Use case: Third-party marketing tools, analytics platforms</li>
            </ul>
          </div>

          <div className="bg-accent/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Temporary / Session-based</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Expiration: <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">86400</code> (1 day)</li>
              <li>Rotation: Enabled</li>
              <li>Use case: Temporary access, trial periods</li>
            </ul>
          </div>
        </div>
      </Card>
    </>
  );
}

function BulkOperationsHelp() {
  return (
    <>
      <Card title="CSV Bulk Upload">
        <div className="prose prose-sm max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            Bulk operations allow you to process large numbers of identities and aliases efficiently
            using CSV files.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3">CSV Format Requirements</h3>

          <div className="bg-accent/50 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-foreground mb-2">Required Format:</p>
            <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`user_id
user1@example.com
user2@example.com
user3@example.com`}
            </pre>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground mb-4">
            <p><strong className="text-foreground">Header row:</strong> Must contain <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">user_id</code></p>
            <p><strong className="text-foreground">File size:</strong> Maximum 50MB</p>
            <p><strong className="text-foreground">Row limit:</strong> Maximum 10,000 rows</p>
            <p><strong className="text-foreground">Processing timeout:</strong> 25 seconds</p>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3">Upload Process</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Navigate to <Link to="/bulk" className="text-primary hover:underline">Bulk Operations</Link></li>
            <li>Select the CSV Upload tab</li>
            <li>Choose your CSV file</li>
            <li>Select one or more contexts for alias creation</li>
            <li>Click "Upload and Process"</li>
            <li>Monitor progress in <Link to="/bulk/jobs" className="text-primary hover:underline">Job History</Link></li>
          </ol>

          <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">Job Statuses</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-muted text-muted-foreground">
                queued
              </span>
              <span className="text-sm text-muted-foreground">Job is waiting to be processed</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-primary/10 text-primary">
                processing
              </span>
              <span className="text-sm text-muted-foreground">Job is currently being processed</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-success/10 text-success">
                completed
              </span>
              <span className="text-sm text-muted-foreground">All items processed successfully</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-warning/10 text-warning">
                partial
              </span>
              <span className="text-sm text-muted-foreground">Some items failed</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-destructive/10 text-destructive">
                failed
              </span>
              <span className="text-sm text-muted-foreground">Job failed to process</span>
            </div>
          </div>
        </div>
      </Card>

      <Card title="JSON Bulk Operations">
        <div className="prose prose-sm max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            For programmatic bulk operations, use the JSON Bulk interface to process up to 100 items at once.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3">Available Operations</h3>

          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Create Identities</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Create multiple identities with aliases in a single request.
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`["user1@example.com", "user2@example.com"]`}
              </pre>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Create Aliases</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Create aliases for existing identities.
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`["user1@example.com", "user2@example.com"]`}
              </pre>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Resolve Aliases</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Resolve multiple alias IDs back to user IDs.
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`["alias-id-1", "alias-id-2"]`}
              </pre>
            </div>
          </div>

          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-4">
            <p className="text-sm text-warning font-medium">
              Maximum 100 items per JSON bulk operation
            </p>
          </div>
        </div>
      </Card>
    </>
  );
}

function APIHelp() {
  return (
    <>
      <Card title="API Integration">
        <div className="prose prose-sm max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            Varinym provides a RESTful API for programmatic access. Use API keys to authenticate your requests.
          </p>

          <h3 className="text-lg font-semibold text-foreground mb-3">Getting Started</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground mb-6">
            <li>Navigate to <Link to="/settings" className="text-primary hover:underline">Settings → API Keys</Link></li>
            <li>Click "Generate New API Key"</li>
            <li>Copy your API key (shown only once)</li>
            <li>Store it securely</li>
          </ol>

          <h3 className="text-lg font-semibold text-foreground mb-3">Authentication</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Include your API key in the request header:
          </p>
          <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto mb-6">
{`X-API-Key: your-api-key-here`}
          </pre>

          <h3 className="text-lg font-semibold text-foreground mb-3">API Endpoints</h3>

          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Create Identity</h4>
              <p className="text-xs text-muted-foreground mb-2">
                <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-mono">POST</code>{' '}
                <code className="px-1.5 py-0.5 bg-muted rounded font-mono">/identities</code>
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "user_id": "user@example.com",
  "context": "default"
}`}
              </pre>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Get Aliases</h4>
              <p className="text-xs text-muted-foreground mb-2">
                <code className="px-1.5 py-0.5 bg-success/10 text-success rounded font-mono">GET</code>{' '}
                <code className="px-1.5 py-0.5 bg-muted rounded font-mono">/aliases/:aliasId</code>
              </p>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Bulk Create Identities</h4>
              <p className="text-xs text-muted-foreground mb-2">
                <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-mono">POST</code>{' '}
                <code className="px-1.5 py-0.5 bg-muted rounded font-mono">/bulk/identities</code>
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "user_ids": ["user1@example.com", "user2@example.com"],
  "context": "default"
}`}
              </pre>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Resolve Aliases</h4>
              <p className="text-xs text-muted-foreground mb-2">
                <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded font-mono">POST</code>{' '}
                <code className="px-1.5 py-0.5 bg-muted rounded font-mono">/bulk/resolve</code>
              </p>
              <pre className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
{`{
  "alias_ids": ["diff-id-1", "diff-id-2"]
}`}
              </pre>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Rate Limits & Best Practices">
        <div className="space-y-4">
          <div className="bg-accent/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Rate Limits</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Single operations: 100 requests/minute</li>
              <li>Bulk operations: 10 requests/minute</li>
              <li>CSV uploads: 5 uploads/hour</li>
            </ul>
          </div>

          <div className="bg-accent/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Best Practices</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Use bulk endpoints for multiple operations</li>
              <li>Implement exponential backoff for retries</li>
              <li>Cache alias IDs when possible</li>
              <li>Rotate API keys regularly</li>
              <li>Never commit API keys to version control</li>
            </ul>
          </div>
        </div>
      </Card>
    </>
  );
}

function SecurityHelp() {
  return (
    <>
      <Card title="Security & Privacy">
        <div className="prose prose-sm max-w-none">
          <h3 className="text-lg font-semibold text-foreground mb-3">Data Protection</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Varinym is designed with privacy and security as core principles. Here's how we protect your data:
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex gap-3">
              <Lock className="h-5 w-5 shrink-0 text-foreground" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">End-to-End Encryption</h4>
                <p className="text-sm text-muted-foreground">
                  All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <KeyRound className="h-5 w-5 shrink-0 text-foreground" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">One-Way Hashing</h4>
                <p className="text-sm text-muted-foreground">
                  Alias IDs use cryptographic hashing. Original user IDs cannot be
                  reverse-engineered from alias IDs without access to Varinym.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Shield className="h-5 w-5 shrink-0 text-foreground" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Context Isolation</h4>
                <p className="text-sm text-muted-foreground">
                  Each context generates unique aliases. A compromise in one context doesn't
                  affect others.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Timer className="h-5 w-5 shrink-0 text-foreground" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Time-Limited Access</h4>
                <p className="text-sm text-muted-foreground">
                  Configure aliases to expire automatically, limiting exposure windows.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <RefreshCw className="h-5 w-5 shrink-0 text-foreground" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Automatic Rotation</h4>
                <p className="text-sm text-muted-foreground">
                  Enable auto-rotation to regularly regenerate aliases for enhanced security.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-3">Compliance</h3>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground mb-6">
            <li>GDPR compliant - Right to be forgotten, data portability</li>
            <li>CCPA compliant - California privacy rights</li>
            <li>SOC 2 Type II certified</li>
            <li>Regular security audits and penetration testing</li>
          </ul>

          <h3 className="text-lg font-semibold text-foreground mb-3">API Key Security</h3>
          <div className="space-y-3">
            <div className="flex gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <span className="text-muted-foreground">Store API keys in environment variables</span>
            </div>
            <div className="flex gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <span className="text-muted-foreground">Use different keys for dev/staging/production</span>
            </div>
            <div className="flex gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <span className="text-muted-foreground">Rotate keys every 90 days</span>
            </div>
            <div className="flex gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <span className="text-muted-foreground">Revoke keys immediately if compromised</span>
            </div>
            <div className="flex gap-2 text-sm">
              <X className="h-4 w-4 shrink-0 text-destructive" />
              <span className="text-muted-foreground">Never commit keys to version control</span>
            </div>
            <div className="flex gap-2 text-sm">
              <X className="h-4 w-4 shrink-0 text-destructive" />
              <span className="text-muted-foreground">Never share keys via email or chat</span>
            </div>
          </div>
        </div>
      </Card>

      <Card title="Reporting Security Issues">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h4 className="font-semibold text-foreground mb-2">Found a security vulnerability?</h4>
          <p className="text-sm text-muted-foreground mb-3">
            We take security seriously. Please report vulnerabilities responsibly:
          </p>
          <div className="text-sm">
            <p className="text-muted-foreground mb-1">Email: <a href="mailto:security@varinym.com" className="text-primary hover:underline">security@varinym.com</a></p>
            <p className="text-muted-foreground">We aim to respond within 48 hours</p>
          </div>
        </div>
      </Card>
    </>
  );
}

function TroubleshootingHelp() {
  return (
    <>
      <Card title="Common Issues & Solutions">
        <div className="space-y-6">
          <div className="border-b border-border pb-4">
            <h4 className="font-semibold text-foreground mb-2">"At least one context required" error</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> Creating identities or aliases without selecting a context</p>
            <p className="text-sm text-success"><strong>Solution:</strong> Select at least one context from the contexts list. The "default" context is automatically selected.</p>
          </div>

          <div className="border-b border-border pb-4">
            <h4 className="font-semibold text-foreground mb-2">CSV upload fails</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> CSV file is rejected or fails to process</p>
            <div className="text-sm space-y-1">
              <p className="text-success"><strong>Solutions:</strong></p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Ensure header row is exactly <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">user_id</code></li>
                <li>Check file size is under 50MB</li>
                <li>Verify row count is under 10,000</li>
                <li>Remove any special characters or formatting</li>
                <li>Save as UTF-8 encoded CSV</li>
              </ul>
            </div>
          </div>

          <div className="border-b border-border pb-4">
            <h4 className="font-semibold text-foreground mb-2">Job stuck in "processing" status</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> Bulk job doesn't complete</p>
            <p className="text-sm text-success"><strong>Solution:</strong> Jobs have a 25-second timeout. If processing takes longer, the job will fail. Try splitting large files into smaller batches.</p>
          </div>

          <div className="border-b border-border pb-4">
            <h4 className="font-semibold text-foreground mb-2">API authentication fails</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> API requests return 401 Unauthorized</p>
            <div className="text-sm space-y-1">
              <p className="text-success"><strong>Solutions:</strong></p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Verify API key is included in header as <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">X-API-Key</code></li>
                <li>Check that the API key hasn't been revoked</li>
                <li>Ensure no extra spaces in the key</li>
                <li>Generate a new API key if needed</li>
              </ul>
            </div>
          </div>

          <div className="border-b border-border pb-4">
            <h4 className="font-semibold text-foreground mb-2">Alias lookup returns no results</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> Cannot resolve an alias ID</p>
            <div className="text-sm space-y-1">
              <p className="text-success"><strong>Solutions:</strong></p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Verify the alias ID is correct (copy-paste to avoid typos)</li>
                <li>Check if the alias has expired</li>
                <li>Ensure you're in the correct customer account</li>
              </ul>
            </div>
          </div>

          <div className="pb-4">
            <h4 className="font-semibold text-foreground mb-2">Cannot delete context</h4>
            <p className="text-sm text-muted-foreground mb-2"><strong>Problem:</strong> Context deletion is disabled</p>
            <p className="text-sm text-success"><strong>Solution:</strong> The "default" context cannot be deleted. For other contexts, you can deactivate them instead of deleting.</p>
          </div>
        </div>
      </Card>

      <Card title="Getting More Help">
        <div className="text-sm space-y-4">
          <p className="text-muted-foreground">
            If you're still experiencing issues after trying these solutions:
          </p>
          <div className="bg-accent/50 rounded-lg p-4">
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Check the <button type="button" onClick={() => setActiveCategory('faq')} className="text-primary hover:underline">FAQ section</button> for more answers</li>
              <li>Review the <button type="button" onClick={() => setActiveCategory('api')} className="text-primary hover:underline">API documentation</button></li>
              <li>Contact support at <a href="mailto:support@varinym.com" className="text-primary hover:underline">support@varinym.com</a></li>
            </ol>
          </div>
          <p className="text-muted-foreground">
            When contacting support, please include:
          </p>
          <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
            <li>Description of the issue</li>
            <li>Steps to reproduce</li>
            <li>Any error messages</li>
            <li>Job IDs (if applicable)</li>
          </ul>
        </div>
      </Card>
    </>
  );

  function setActiveCategory(category: string) {
    // Helper function for FAQ link
    window.dispatchEvent(new CustomEvent('changeHelpCategory', { detail: category }));
  }
}

function FAQ() {
  const faqs = [
    {
      q: "What's the difference between an identity and an alias?",
      a: "An identity is the core record representing a user, identified by your user ID. An alias is a context-specific masked identifier derived from an identity. You can have multiple aliases per identity, one for each context."
    },
    {
      q: "Can I use Varinym for GDPR compliance?",
      a: "Yes! Varinym helps with GDPR compliance by masking user identifiers and providing context isolation. You can delete identities (right to be forgotten) and export data (data portability). However, Varinym is just one part of GDPR compliance - consult legal counsel for comprehensive compliance."
    },
    {
      q: "What happens when an alias expires?",
      a: "When an alias expires, it can no longer be used. If auto-rotation is enabled on the context, a new alias will be automatically generated when the old one is accessed. If auto-rotation is disabled, you'll need to manually create a new alias."
    },
    {
      q: "Can I change a context's expiration time after creation?",
      a: "Yes, you can update context settings at any time. However, changes only affect new aliases - existing aliases retain their original expiration time."
    },
    {
      q: "How many API keys can I create?",
      a: "You can create unlimited API keys. We recommend creating separate keys for different environments (dev, staging, production) and rotating them regularly."
    },
    {
      q: "What's the maximum file size for CSV uploads?",
      a: "CSV files must be under 50MB and contain no more than 10,000 rows. For larger datasets, split them into multiple files."
    },
    {
      q: "Can I bulk delete identities?",
      a: "Currently, bulk deletion is not available through the UI. Contact support if you need to delete large numbers of identities."
    },
    {
      q: "Are alias IDs globally unique?",
      a: "Yes, alias IDs are globally unique across all customers and contexts. You can safely use them as unique identifiers."
    },
    {
      q: "Can I recover a revoked API key?",
      a: "No, once an API key is revoked, it cannot be recovered. You'll need to generate a new API key."
    },
    {
      q: "What happens to aliases when I delete an identity?",
      a: "All aliases associated with an identity are also deleted when the identity is deleted. This action cannot be undone."
    },
    {
      q: "Can I export my data?",
      a: "Yes, you can export identities and aliases via the API. Contact support if you need help with bulk exports."
    },
    {
      q: "Is there a rate limit for API requests?",
      a: "Yes. Single operations: 100 req/min, bulk operations: 10 req/min, CSV uploads: 5 uploads/hour. Contact support for higher limits."
    },
    {
      q: "Can I test Varinym before integrating?",
      a: "Absolutely! Use the web interface to test all features. You can also use the JSON Bulk operations tab for testing API-like interactions."
    },
    {
      q: "What regions is Varinym available in?",
      a: "Varinym is available globally with data centers in US, EU, and APAC. Contact support to specify your preferred region for data residency."
    },
    {
      q: "How do I invite team members?",
      a: "Navigate to Settings → Invitations and send an invite by email. You can assign a role: Owner (manages users and roles), Admin (full access to all features), or User (can view and create identities)."
    }
  ];

  return (
    <Card title="Frequently Asked Questions">
      <div className="space-y-6">
        {faqs.map((faq, index) => (
          <div key={index} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
            <h4 className="font-semibold text-foreground mb-2">{faq.q}</h4>
            <p className="text-sm text-muted-foreground">{faq.a}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}
